// Iter 81 / Phase E0 — Employee Self-Service (ESS) Portal.
// Surfaces the linked employee's profile + editable personal details +
// own document repository (uploaded + generated). Iter 81.1 / Phase E0.1
// adds: Pending Acceptances card, Acceptance modal (Accept / Decline),
// and the auto-injected audit stamp surface on accepted documents.
import { useCallback, useEffect, useState } from "react";
import { FloppyDisk, IdentificationCard, FileText, CheckCircle, XCircle, ShieldCheck } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const Row = ({ k, v }) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0">
        <div className="text-xs text-slate-500">{k}</div>
        <div className="col-span-2 text-sm text-slate-800">{v || <span className="text-slate-300">—</span>}</div>
    </div>
);

export default function EssMe() {
    const [emp, setEmp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [unlinked, setUnlinked] = useState(false);
    const [form, setForm] = useState({ mobile: "", alternate_mobile: "", personal_email: "", current_address: "", emergency_contact: { name: "", number: "", relationship: "" } });
    const [saving, setSaving] = useState(false);
    const [docs, setDocs] = useState({ documents: [], generated_documents: [] });
    // Iter 81.1 / Phase E0.1 — Pending acceptances + acceptance modal.
    const [pending, setPending] = useState({ pending: [], accepted: [] });
    const [acceptDoc, setAcceptDoc] = useState(null);
    const [acceptRendered, setAcceptRendered] = useState("");
    const [acceptRemarks, setAcceptRemarks] = useState("");
    const [acceptAck, setAcceptAck] = useState(false);
    const [acceptBusy, setAcceptBusy] = useState(false);
    const [declineMode, setDeclineMode] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/hr/ess/me");
            setEmp(data);
            setForm({
                mobile: data.mobile || "",
                alternate_mobile: data.alternate_mobile || "",
                personal_email: data.personal_email || "",
                current_address: data.current_address || "",
                emergency_contact: data.emergency_contact || { name: "", number: "", relationship: "" },
            });
            setUnlinked(false);
            // Load own documents
            const docsRes = await api.get("/hr/ess/me/documents");
            setDocs(docsRes.data);
            // Load pending acceptances
            const pendingRes = await api.get("/hr/ess/me/pending-acceptances");
            setPending(pendingRes.data);
        } catch (err) {
            if (err?.response?.status === 404) {
                setUnlinked(true);
            } else {
                toast.error(formatApiError(err, "Failed to load ESS profile"));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        Promise.resolve().then(() => { reload(); });
    }, [reload]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/hr/ess/me/personal-details", form);
            toast.success("Personal details updated.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        } finally {
            setSaving(false);
        }
    };

    // Iter 81.1 / Phase E0.1 — Open the acceptance dialog. Loads the
    // rendered document (so the stamp + body display), then records a
    // view event on the document (non-blocking).
    const openAccept = async (doc) => {
        setAcceptDoc(doc);
        setAcceptRendered("");
        setAcceptRemarks("");
        setAcceptAck(false);
        setDeclineMode(false);
        try {
            const { data } = await api.get(`/hr/document/${doc.id}`);
            setAcceptRendered(data.rendered_html || "");
            api.post(`/hr/ess/me/document/${doc.id}/view`).catch(() => {});
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load document"));
        }
    };

    const doEssAccept = async () => {
        if (!acceptAck) return toast.error("Please tick the acknowledgement checkbox.");
        setAcceptBusy(true);
        try {
            await api.post(`/hr/ess/me/document/${acceptDoc.id}/accept`, { remarks: acceptRemarks });
            toast.success("Document accepted.");
            setAcceptDoc(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Acceptance failed"));
        } finally {
            setAcceptBusy(false);
        }
    };

    const doEssDecline = async () => {
        if (acceptRemarks.trim().length < 3) return toast.error("Please share a reason for declining.");
        setAcceptBusy(true);
        try {
            await api.post(`/hr/ess/me/document/${acceptDoc.id}/decline`, { remarks: acceptRemarks });
            toast.success("Document declined.");
            setAcceptDoc(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Decline failed"));
        } finally {
            setAcceptBusy(false);
        }
    };

    if (loading) return <div className="px-6 sm:px-8 py-12 text-sm text-slate-500">Loading…</div>;

    if (unlinked) {
        return (
            <div className="px-6 sm:px-8 py-6 max-w-[900px]" data-testid="ess-unlinked">
                <PageHeader eyebrow="Employee Self-Service" title="My Profile" />
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-800">
                    Your login is not linked to any employee record yet.
                    Please contact HR to have your account linked, after which you&apos;ll be able to view and update your personal details here.
                </div>
            </div>
        );
    }

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1100px]" data-testid="ess-me-page">
            <PageHeader
                eyebrow="Employee Self-Service"
                title="My Profile"
                description={(
                    <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="font-mono">{emp.employee_code}</Badge>
                        <Badge variant="outline">{emp.employment_category}</Badge>
                        <Badge data-testid="ess-status-badge">{emp.status}</Badge>
                    </div>
                )}
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ess-read-only-section">
                    <h3 className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2"><IdentificationCard size={16} /> Read-only Information</h3>
                    <Row k="Name" v={emp.full_name} />
                    <Row k="Official Email" v={emp.official_email} />
                    <Row k="Department" v={emp.department} />
                    <Row k="Designation" v={emp.designation} />
                    <Row k="Date of Joining" v={emp.date_of_joining} />
                    <Row k="Confirmation Date" v={emp.confirmation_date} />
                    <Row k="Notice Period" v={`${emp.notice_period_days || 0} days`} />
                    <Row k="Reporting Manager" v={emp.reporting_manager ? `${emp.reporting_manager.full_name} (${emp.reporting_manager.employee_code})` : "—"} />
                    <Row k="RM Email" v={emp.reporting_manager?.official_email} />
                    <div className="text-[11px] text-slate-400 mt-3 italic">
                        Banking, identity (PAN/Aadhaar), and statutory fields can only be updated by HR. Please raise a request if any of these need correction.
                    </div>
                </section>

                <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ess-editable-section">
                    <h3 className="text-sm font-semibold mb-3 text-slate-700">Update My Details</h3>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Mobile</Label>
                            <Input data-testid="ess-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Alternate Mobile</Label>
                            <Input value={form.alternate_mobile} onChange={(e) => setForm({ ...form, alternate_mobile: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Personal Email</Label>
                            <Input data-testid="ess-personal-email" type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Current Address</Label>
                            <Textarea data-testid="ess-current-address" rows={3} value={form.current_address} onChange={(e) => setForm({ ...form, current_address: e.target.value })} />
                        </div>
                        <div className="border border-slate-200 rounded-md p-3 space-y-2">
                            <div className="text-xs font-semibold text-slate-700">Emergency Contact</div>
                            <Input placeholder="Name" data-testid="ess-emergency-name" value={form.emergency_contact.name} onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, name: e.target.value } })} />
                            <Input placeholder="Number" data-testid="ess-emergency-number" value={form.emergency_contact.number} onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, number: e.target.value } })} />
                            <Input placeholder="Relationship" value={form.emergency_contact.relationship} onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, relationship: e.target.value } })} />
                        </div>
                        <Button data-testid="ess-save-btn" onClick={save} disabled={saving} className="w-full">
                            <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save Changes"}
                        </Button>
                    </div>
                </section>
            </div>

            <section className="mt-6 bg-white rounded-xl border border-amber-200 p-5" data-testid="ess-pending-acceptances">
                <h3 className="text-sm font-semibold mb-3 text-amber-800 flex items-center gap-2"><ShieldCheck size={16} /> Pending Acceptances {pending.pending.length > 0 && <Badge className="bg-amber-100 text-amber-700">{pending.pending.length}</Badge>}</h3>
                {pending.pending.length === 0 ? (
                    <div className="text-xs text-slate-400">No documents pending your acceptance. ✓</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Title</th>
                                <th className="text-left px-3 py-2 font-medium">Category</th>
                                <th className="text-left px-3 py-2 font-medium">Shared</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="w-32" />
                            </tr>
                        </thead>
                        <tbody>
                            {pending.pending.map((d) => (
                                <tr key={d.id} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-slate-800">{d.rendered_title}</td>
                                    <td className="px-3 py-2"><Badge variant="outline">{d.category}</Badge></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(d.shared_at)}</td>
                                    <td className="px-3 py-2"><Badge className={d.share_status === "Viewed" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}>{d.share_status}</Badge></td>
                                    <td className="px-3 py-2 text-right">
                                        <Button size="sm" data-testid={`ess-accept-open-${d.id}`} onClick={() => openAccept(d)} className="gap-1"><ShieldCheck size={12} /> Review</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="ess-documents-section">
                <h3 className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2"><FileText size={16} /> My Documents</h3>

                <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2">Uploaded Documents</h4>
                {docs.documents.length === 0 ? (
                    <div className="text-xs text-slate-400 mb-4">No documents uploaded yet.</div>
                ) : (
                    <table className="w-full text-sm mb-4">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-left px-3 py-2 font-medium">File</th>
                                <th className="text-left px-3 py-2 font-medium">Uploaded</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.documents.map((d) => (
                                <tr key={d.id} className="border-t border-slate-100">
                                    <td className="px-3 py-2"><Badge variant="outline">{d.document_type}</Badge></td>
                                    <td className="px-3 py-2"><a href={d.file_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{d.file_name}</a></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(d.uploaded_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2 mt-4">Generated Documents (Offer / NDA / Appointment etc.)</h4>
                {docs.generated_documents.length === 0 ? (
                    <div className="text-xs text-slate-400">No generated documents yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Title</th>
                                <th className="text-left px-3 py-2 font-medium">Category</th>
                                <th className="text-left px-3 py-2 font-medium">Generated</th>
                                <th className="w-24" />
                            </tr>
                        </thead>
                        <tbody>
                            {docs.generated_documents.slice().reverse().map((d) => (
                                <tr key={d.id} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-slate-700">{d.rendered_title}</td>
                                    <td className="px-3 py-2"><Badge variant="outline">{d.category}</Badge></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(d.generated_at)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <Button size="sm" variant="outline" onClick={async () => {
                                            try {
                                                const { data } = await api.get(`/hr/document/${d.id}`);
                                                const w = window.open("", "_blank");
                                                if (w) {
                                                    w.document.write(`<html><head><title>${data.rendered_title}</title></head><body>${data.rendered_html}</body></html>`);
                                                    w.document.close();
                                                }
                                            } catch (err) {
                                                toast.error(formatApiError(err, "Failed to load document"));
                                            }
                                        }}>View</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Iter 81.1 — ESS Document Acceptance Modal */}
            <Dialog open={!!acceptDoc} onOpenChange={(o) => { if (!o) { setAcceptDoc(null); setAcceptRendered(""); setAcceptRemarks(""); setAcceptAck(false); setDeclineMode(false); } }}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="ess-accept-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><ShieldCheck size={16} className="text-indigo-600" /> {acceptDoc?.rendered_title}</DialogTitle>
                    </DialogHeader>
                    {acceptDoc && (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                                <Badge variant="outline">{acceptDoc.category}</Badge>
                                <span>v{acceptDoc.template_version}</span>
                                {acceptDoc.issue_date && <><span>·</span><span>Issued {acceptDoc.issue_date}</span></>}
                                {acceptDoc.effective_date && <><span>·</span><span>Effective {acceptDoc.effective_date}</span></>}
                            </div>
                            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-[45vh] overflow-y-auto" data-testid="ess-accept-rendered" dangerouslySetInnerHTML={{ __html: acceptRendered }} />
                            {!declineMode ? (
                                <>
                                    <label className="flex items-start gap-2 text-sm text-slate-700">
                                        <input type="checkbox" data-testid="ess-accept-ack" checked={acceptAck} onChange={(e) => setAcceptAck(e.target.checked)} className="mt-1" />
                                        <span>I have read and accept this document. (Acceptance is recorded with a timestamp and your IP address — visible only to HR.)</span>
                                    </label>
                                    <Textarea data-testid="ess-accept-remarks" placeholder="Optional remarks" value={acceptRemarks} onChange={(e) => setAcceptRemarks(e.target.value)} rows={2} />
                                </>
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-sm font-semibold text-slate-800">Reason for declining</div>
                                    <Textarea data-testid="ess-decline-remarks" placeholder="Required — minimum 3 characters" value={acceptRemarks} onChange={(e) => setAcceptRemarks(e.target.value)} rows={3} />
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        {!declineMode ? (
                            <>
                                <Button variant="outline" onClick={() => setAcceptDoc(null)}>Cancel</Button>
                                <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setDeclineMode(true)} data-testid="ess-decline-open"><XCircle size={14} className="mr-1" /> Decline</Button>
                                <Button onClick={doEssAccept} disabled={!acceptAck || acceptBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ess-accept-confirm"><CheckCircle size={14} className="mr-1" /> Accept</Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setDeclineMode(false)}>Back</Button>
                                <Button onClick={doEssDecline} disabled={acceptRemarks.trim().length < 3 || acceptBusy} className="bg-rose-600 hover:bg-rose-700" data-testid="ess-decline-confirm"><XCircle size={14} className="mr-1" /> Confirm Decline</Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
